import React from 'react'
import * as R from 'ramda'
import { Typography } from '@material-ui/core'
import { ServiceResponse } from '@mutual-libs/shared-types'
import { ProposalRisk } from '@mutual-services/credit-score-pj-types'
import { DocumentsGroupType, DocumentType, ContentType, DocumentsGroup, Document, DocumentAnalysisStatus } from '@mutual-services/account-documents-types'
import { Bold } from '~/components'
import { mutualTheme } from './theme'
import { formatToRealStr } from './format'

export const getDocumentDescriptionByType = (type: DocumentsGroupType, proposalRisk: ProposalRisk, monthlyIncome: number) => {
  switch (type) {
    case DocumentsGroupType.ARTICLES_OF_INCORPORATION:
      return (
        <>
          <Typography variant='airbnbBody1'>
            Envie o documento mais recente registrado e assinado pelos envolvidos.
          </Typography>
          <Typography variant='airbnbBody1'>
            <Bold>Observação:</Bold> Caso não possua Contrato Social, poderá ser enviado o <Bold>Requerimento de Empresário.</Bold>
          </Typography>
        </>
      )
    case DocumentsGroupType.BANK_STATEMENT:
      return (
        <Typography>
          Envie os extratos bancários dos últimos {proposalRisk === ProposalRisk.DENIED ? 5 : 3} meses completos que ajudem a comprovar o faturamento informado (<Bold>{formatToRealStr(monthlyIncome, false)}</Bold>). Você pode desconsiderar o mês atual (ainda em aberto). Você pode enviar os extratos de várias contas diferentes ou dar preferência para a conta bancária principal da sua empresa, caso ela concentre a maioria das movimentações.
        </Typography>
      )
    case DocumentsGroupType.SIMPLES_NACIONAL:
      return <Typography>Faça o upload dos últimos 5 meses do Simples Nacional da empresa. Você pode enviar o extrato consolidado ou um arquivo separado para cada mês, como for de sua preferência.</Typography>
    default:
      return type
  }
}

export const getFileTypes = (fileTypes: DocumentType[]): ContentType[] => {
  const types = [] as ContentType[]

  fileTypes.forEach(file => {
    if (file === DocumentType.document) {
      types.push(ContentType.PDF)
    }
    if (file === DocumentType.photo) {
      types.push(ContentType.PNG)
      types.push(ContentType.JPEG)
    }
    if (file === DocumentType.video) {
      types.push(ContentType.AVI)
      types.push(ContentType.MPG)
      types.push(ContentType.MP4)
    }
  })

  return types
}

export const translateDocumentType = (documentationType: DocumentsGroupType) => {
  switch (documentationType) {
    case DocumentsGroupType.ADDITIONAL:
      return 'Outros'
    case DocumentsGroupType.ARTICLES_OF_INCORPORATION:
      return 'Contrato social'
    case DocumentsGroupType.BANK_STATEMENT:
      return 'Extrato bancário'
    case DocumentsGroupType.SIMPLES_NACIONAL:
      return 'Simples nacional'
    default:
      return documentationType
  }
}

export type FetchRequestDocumentsOutput = ServiceResponse<Document>

export const fetchRequestedDocuments = (documents: ServiceResponse<DocumentsGroup[]>): FetchRequestDocumentsOutput[] => {
  const requestedDocuments = [] as FetchRequestDocumentsOutput[]
  documents.forEach(document => {
    document.documents.map(item => {
      if (!R.isEmpty(item)) {
        requestedDocuments.push(item)
      }
    })
  })

  return requestedDocuments
}

export const getFileType = (fileType: string): ContentType => {
  switch (fileType) {
    case 'application/pdf':
      return ContentType.PDF
    case 'text/csv':
      return ContentType.CSV
    case 'image/png':
      return ContentType.PNG
    case 'image/jpeg':
      return ContentType.JPEG
    case 'application/zip':
      return ContentType.ZIP
    case 'text/plain':
      return ContentType.PLAIN_TEXT
    case 'application/octet-stream':
      return ContentType.OCTET_STREAM
    default:
      return ContentType.PDF
  }
}

export const getDocumentPendencyStatusColor = (status: DocumentAnalysisStatus) => {
  switch (status) {
    case DocumentAnalysisStatus.APPROVED:
      return mutualTheme.palette.badgeBackgroundColor.success.light
    case DocumentAnalysisStatus.PENDING:
      return mutualTheme.palette.badgeBackgroundColor.error.light
    case DocumentAnalysisStatus.PENDING_ANALYSIS:
      return mutualTheme.palette.primary.main
    default:
      return mutualTheme.palette.primary.main
  }
}

export const translateDocumentPendencyStatus = (status: DocumentAnalysisStatus) => {
  switch (status) {
    case DocumentAnalysisStatus.APPROVED:
      return 'Resolvido'
    case DocumentAnalysisStatus.PENDING:
      return 'Pendente'
    case DocumentAnalysisStatus.PENDING_ANALYSIS:
      return 'Em validação'
    default:
      return 'Em validação'
  }
}

export const getRequiredNumberOfDocuments = (documentType: DocumentsGroupType): number | 'unlimited' => {
  switch (documentType) {
    case DocumentsGroupType.ADDITIONAL:
      return 1
    case DocumentsGroupType.ARTICLES_OF_INCORPORATION:
      return 1
    case DocumentsGroupType.BANK_STATEMENT:
      return 4
    case DocumentsGroupType.SIMPLES_NACIONAL:
      return 4
    default:
      return 1
  }
}

export type BaseDocumentsValidation = {
  [K in DocumentsGroupType | string]: number
}

const defaultRequiredDocumentsQuantity = (types: DocumentsGroupType[]) => types.reduce((acc: BaseDocumentsValidation, item) => {
  return { ...acc, [item]: 0 }
}, {} as BaseDocumentsValidation)

export const checkForMissingRequiredDocuments = (updatedDocs: FetchRequestDocumentsOutput[], documentsList: ServiceResponse<DocumentsGroup[]>) => {
  const inputsToValidate = documentsList.map(item => item.type === DocumentsGroupType.ADDITIONAL ? (item.typeName || item.type) : item.type) as DocumentsGroupType[]
  const inputsQuantity = defaultRequiredDocumentsQuantity(inputsToValidate)
  const additionalDocuments = documentsList.filter(doc => doc.type === DocumentsGroupType.ADDITIONAL)
  const additionalDocumentsQuantity = !R.isEmpty(additionalDocuments) ? additionalDocuments[0].quantity : 0

  const numberOfUploadedDocumentsPerType = updatedDocs.reduce((acc, document) => {
    const { type } = document
    const additionalDocument = documentsList.find(item => item.documents.some(doc => doc.key === document.key))
    const isAdditionalDocument = additionalDocument ? additionalDocument.type === DocumentsGroupType.ADDITIONAL : false
    const documentType = isAdditionalDocument ? (additionalDocument ? (additionalDocument.typeName || type) : type) : type

    return {
      ...acc,
      [documentType]: acc[documentType] + 1
    }
  }, inputsQuantity)

  const convertedObjectToArrayofDocumentsType: { name: DocumentsGroupType, value: boolean}[] = []

  Object.entries(numberOfUploadedDocumentsPerType).forEach((item) => {
    const validatedInput = item[0] === DocumentsGroupType.ADDITIONAL ? additionalDocumentsQuantity : getRequiredNumberOfDocuments(item[0] as DocumentsGroupType)
    const formatedValidatedInput = validatedInput >= 4 ? 1 : validatedInput
    if (!isNaN(item[1])) {
      convertedObjectToArrayofDocumentsType.push({ name: item[0] as DocumentsGroupType, value: item[1] >= formatedValidatedInput })
    }
  })

  return convertedObjectToArrayofDocumentsType.filter((item) => item.value === false).length > 0
}

export const isTabCompleted = (document: ServiceResponse<DocumentsGroup>, updatedDocs: FetchRequestDocumentsOutput[]) => {
  const currentDocumentGroupType = document.type
  const isAdditionalDocument = currentDocumentGroupType === DocumentsGroupType.ADDITIONAL
  const requiredNumberOfDocumentsPerType = isAdditionalDocument ? document.quantity : getRequiredNumberOfDocuments(currentDocumentGroupType)
  const formatedRequiredNumberOfDocumentsPerType = requiredNumberOfDocumentsPerType >= 4 ? 1 : requiredNumberOfDocumentsPerType

  const quantityOfSentDocumentsPerType = updatedDocs.filter(doc => isAdditionalDocument
    ? document.documents.some(item => item.key === doc.key)
    : doc.type === currentDocumentGroupType).length

  if (quantityOfSentDocumentsPerType >= formatedRequiredNumberOfDocumentsPerType) {
    return true
  }

  return false
}

export const documentsListOrder = ['articlesOfCorporation', 'bankStatement', 'simplesNacional', 'balanceSheet', 'DRE', 'companyPhoto', 'companyVideo', 'additional']

export const orderDocumentsGroupList = (documentsGroupList: ServiceResponse<DocumentsGroup[] | null>) => {
  if (!documentsGroupList) {
    return []
  }

  return documentsGroupList.sort((a, b) => {
    return documentsListOrder.indexOf(a.type) - documentsListOrder.indexOf(b.type)
  })
}

export type RequiredDocument = {
  title: string,
  description: string
}

const DeniedDocuments: RequiredDocument[] =
[
  {
    title: 'Contrato Social',
    description: 'Ou requerimento do empresário'
  },
  {
    title: 'Extrato do Simples',
    description: 'Dos últimos 5 meses'
  },
  {
    title: 'Extrato bancário',
    description: 'Dos últimos 5 meses'
  }
]

const MediumRiskDocuments: RequiredDocument[] = [
  {
    title: 'Contrato Social',
    description: 'Ou requerimento do empresário'
  },
  {
    title: 'Extrato bancário',
    description: 'Dos últimos 3 meses'
  }
]

const lowRiskDocuments: RequiredDocument[] = [
  {
    title: 'Contrato Social',
    description: 'Ou requerimento do empresário'
  }
]

export const getRequiredDocumentsByProposalRisk = (proposalRisk: ProposalRisk): RequiredDocument[] => {
  if (proposalRisk === ProposalRisk.DENIED) {
    return DeniedDocuments
  }

  if (proposalRisk === ProposalRisk.HIGH || proposalRisk === ProposalRisk.MEDIUM) {
    return MediumRiskDocuments
  }

  return lowRiskDocuments
}
